import { useState } from "react";
import { motion } from "framer-motion";
import { Send, User, Phone, GraduationCap, MessageCircle, ArrowRight } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const DEFAULT_LEVELS = ["SD", "SMP", "SMA", "Persiapan UTBK", "Lainnya"];

export const Registration = ({ content }) => {
  const r = content.registration || { title: "Daftar Sekarang", subtitle: "" };
  const levels = r.levels?.length ? r.levels : DEFAULT_LEVELS;
  const lmsUrl = content.brand?.lmsUrl || "https://www.lms.adhisatyacourse.online";
  const [form, setForm] = useState({ name: "", level: levels[0] || "", phone: "", message: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const message =
      `*Pendaftaran Siswa Baru - ADHISATYA COURSE*\n\n` +
      `Nama: ${form.name}\n` +
      `Jenjang: ${form.level}\n` +
      `No. HP: ${form.phone}\n` +
      `Pesan: ${form.message || "-"}`;
    window.open(`https://wa.me/${content.contact.whatsapp}?text=${encodeURIComponent(message)}`, "_blank");
  };

  return (
    <section
      id="pendaftaran"
      data-testid="registration-section"
      className="py-20 md:py-32 bg-background relative overflow-hidden"
    >
      <div className="absolute inset-0 grid-pattern opacity-50" />
      <div className="relative max-w-3xl mx-auto px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-sky-100 text-sky-700 text-sm font-semibold mb-4">
            Pendaftaran
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{r.title}</h2>
          <p className="mt-4 text-base text-slate-500 leading-relaxed">{r.subtitle}</p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          onSubmit={submit}
          className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/60 p-6 sm:p-10 space-y-5"
        >
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <User className="w-4 h-4 text-sky-500" /> Nama Lengkap Siswa
            </label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              data-testid="reg-name-input"
              placeholder="Masukkan nama lengkap"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <GraduationCap className="w-4 h-4 text-sky-500" /> Jenjang
              </label>
              <select
                value={form.level}
                onChange={(e) => set("level", e.target.value)}
                data-testid="reg-level-select"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400 transition"
              >
                {levels.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Phone className="w-4 h-4 text-sky-500" /> Nomor HP / WhatsApp
              </label>
              <input
                type="tel"
                required
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                data-testid="reg-phone-input"
                placeholder="08xxxxxxxxxx"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <MessageCircle className="w-4 h-4 text-sky-500" /> Pesan / Pertanyaan (opsional)
            </label>
            <textarea
              rows={3}
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              data-testid="reg-message-input"
              placeholder="Tulis pertanyaan atau program yang diminati"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition resize-y"
            />
          </div>

          <button
            type="submit"
            data-testid="reg-submit-button"
            className="w-full inline-flex items-center justify-center gap-2.5 px-6 py-4 rounded-full bg-green-500 hover:bg-green-600 text-white font-bold text-lg shadow-xl shadow-green-500/30 hover:-translate-y-0.5 transition-all"
          >
            <FaWhatsapp className="w-5 h-5" />
            Kirim ke WhatsApp Admin
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">ATAU</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <a
            href={lmsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reg-lms-button"
            className="group w-full inline-flex items-center justify-center gap-2.5 px-6 py-4 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-lg shadow-xl shadow-sky-500/30 hover:-translate-y-0.5 transition-all"
          >
            Daftar Sekarang
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>

          <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1">
            <Send className="w-3 h-3" /> Kirim data via WhatsApp, atau daftar online lewat portal LMS kami
          </p>
        </motion.form>
      </div>
    </section>
  );
};
